import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "When I make a promise to myself (routine, habit, boundary), I usually…",
    options: [
      "Follow through most of the time",
      "Try, but often delay or reschedule",
      "Drop it easily",
    ],
  },
  {
    question: "When honesty feels uncomfortable, I tend to…",
    options: [
      "Still speak my truth respectfully",
      "Soften or adjust the truth",
      "Avoid or hide it",
    ],
  },
  {
    question: "My daily choices generally feel…",
    options: [
      "Aligned with what I believe is right for me",
      "Aligned sometimes, compromised at other times",
      "Mostly driven by convenience or pressure",
    ],
  },
  {
    question: "When I make a mistake, my first instinct is to…",
    options: [
      "Take responsibility and correct it",
      "Justify or explain it",
      "Avoid accountability",
    ],
  },
  {
    question: "Even when no one is watching, I usually…",
    options: [
      "Act the same way I would if someone were",
      "Bend rules occasionally",
      "Take shortcuts",
    ],
  },
  {
    question: "When something goes against my values, I usually…",
    options: [
      "Speak up or step away",
      "Feel uncomfortable but stay quiet",
      "Go along with it",
    ],
  },
  {
    question: "I keep commitments I make to others…",
    options: ["Consistently", "Most of the time", "Only when it’s easy"],
  },
  {
    question: "When I realise I’m wrong, I usually…",
    options: [
      "Admit it openly",
      "Acknowledge it internally",
      "Defend myself",
    ],
  },
  {
    question: "My decisions are influenced by…",
    options: [
      "My values and long-term peace",
      "Situations and emotions",
      "Fear of consequences or opinions",
    ],
  },
  {
    question: "At the end of the day, when I reflect on my actions, I usually feel…",
    options: [
      "At peace with myself",
      "Mixed — some pride, some discomfort",
      "Uneasy or disconnected",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
    <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
      <h1 className="text-2xl font-bold text-primary mb-4">
        Integrity Self-Assessment
      </h1>

      <p className="text-gray-700">
        (Alignment between my values, words, and actions)
      </p>

      <p className="mt-4 text-gray-700">
        I’ll answer based on how I usually show up in everyday life,
        especially when things are inconvenient or uncomfortable.
      </p>

      <Button
        variant="primary"
        size="md"
        className="mt-6"
        text="Start Quiz"
        onClick={onStart}
      />
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let resultText = "";

  if (totalScore >= 15) {
    resultText = `🧭 15–20 points: Strong Inner Integrity
Your actions mostly match your values

You experience inner peace because of this alignment

Focus: continue honouring your truth, even in small moments`;
  } else if (totalScore >= 8) {
    resultText = `🌱 8–14 points: Situational Integrity
You value honesty and alignment, but external pressure influences you

This is common during stress or transitions

Focus: clarity on values + small daily commitments`;
  } else {
    resultText = `🌧️ 0–7 points: Integrity Needs Attention
There may be frequent compromises creating inner discomfort

This is not a flaw — it’s a signal to reconnect with your values

Focus: start with self-honesty and one non-negotiable boundary`;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
      <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Your Integrity Score
        </h2>

        <p className="text-lg font-semibold text-primary">
          Total Score: {totalScore} / 20
        </p>

        <pre className="whitespace-pre-wrap text-left text-gray-700 mt-4">
          {resultText}
        </pre>

        <Button
          variant="primary"
          size="md"
          className="mt-6"
          text="Retake Quiz"
          onClick={onRetake}
        />
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const IntegrityQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default IntegrityQuiz;
